import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Github, 
  Linkedin, 
  Twitter, 
  Mail, 
  Heart,
  Code2,
  ArrowUpRight,
  Terminal,
  Sparkles,
  MapPin,
  Phone,
  Globe,
  Zap,
  Coffee
} from 'lucide-react';

const Footer = () => {
  const { language } = useLanguage();

  const content = {
    ar: {
      description: "مطور ويب متخصص في بناء تطبيقات ويب حديثة وسريعة باستخدام أحدث التقنيات",
      quickLinks: "روابط سريعة",
      home: "الرئيسية",
      about: "من أنا",
      projects: "المشاريع",
      blog: "المدونة",
      contact: "اتصل بنا",
      social: "تابعني",
      services: "الخدمات",
      copyright: "جميع الحقوق محفوظة",
      madeWith: "صنع بـ",
      and: "و",
      by: "بواسطة",
      codeTag: "// بناء المستقبل",
      getInTouch: "تواصل معي",
      contactDesc: "تواصل معي لأي استفسار أو مشروع"
    },
    en: {
      description: "Web developer specializing in building modern and fast web applications using the latest technologies",
      quickLinks: "Quick Links",
      home: "Home",
      about: "About",
      projects: "Projects",
      blog: "Blog",
      contact: "Contact",
      social: "Follow Me",
      services: "Services",
      copyright: "All rights reserved",
      madeWith: "Made with",
      and: "&",
      by: "by",
      codeTag: "// building the future",
      getInTouch: "Get in Touch",
      contactDesc: "Get in touch for any inquiries or projects"
    }
  };

  const t = content[language];

  const socialLinks = [
    { icon: Github, url: "https://github.com", label: "GitHub", gradient: "from-gray-700 to-gray-900", hoverGradient: "hover:from-gray-600 hover:to-gray-800" },
    { icon: Linkedin, url: "https://linkedin.com", label: "LinkedIn", gradient: "from-blue-600 to-blue-800", hoverGradient: "hover:from-blue-500 hover:to-blue-700" },
    { icon: Twitter, url: "https://twitter.com", label: "Twitter", gradient: "from-sky-500 to-blue-600", hoverGradient: "hover:from-sky-400 hover:to-blue-500" },
    { icon: Mail, url: "mailto:info@example.com", label: "Email", gradient: "from-red-500 to-pink-600", hoverGradient: "hover:from-red-400 hover:to-pink-500" }
  ];

  const quickLinks = [
    { name: t.home, path: "/visitor" },
    { name: t.about, path: "/visitor/about" },
    { name: t.projects, path: "/visitor/projects" },
    { name: t.blog, path: "/visitor/blog" },
    { name: t.contact, path: "/visitor/contact" }
  ];

  const services = [
    { name: language === 'ar' ? 'تطوير الويب' : 'Web Development', icon: Globe },
    { name: language === 'ar' ? 'تطبيقات الجوال' : 'Mobile Apps', icon: Phone },
    { name: language === 'ar' ? 'تصميم UI/UX' : 'UI/UX Design', icon: Sparkles },
    { name: language === 'ar' ? 'استشارات تقنية' : 'Tech Consulting', icon: Zap }
  ];

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-950">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, delay: 2 }}
        />
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
        {/* Code decorations */}
        <div className="absolute top-10 left-10 text-white/5 font-mono text-6xl font-bold">{'{ }'}</div>
        <div className="absolute bottom-20 right-20 text-white/5 font-mono text-6xl font-bold">{'</>'}</div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <Link to="/visitor" className="group flex items-center gap-3 mb-6">
              <motion.div
                className="relative p-3 bg-gradient-to-br from-purple-500 via-pink-500 to-blue-500 rounded-2xl shadow-lg shadow-purple-500/25"
                whileHover={{ scale: 1.05, rotate: 5 }}
              >
                <Code2 className="w-7 h-7 text-white" />
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-blue-500 rounded-2xl blur opacity-50 group-hover:opacity-75 transition-opacity" />
              </motion.div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent">
                  Portfolio
                </span>
                <p className="text-xs text-gray-500 font-mono">{t.codeTag}</p>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
              {t.description}
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <motion.a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-3 bg-gradient-to-br ${social.gradient} ${social.hoverGradient} rounded-xl text-white shadow-lg transition-all duration-300`}
                    aria-label={social.label}
                    whileHover={{ scale: 1.1, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <IconComponent className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="flex items-center gap-2 text-lg font-semibold text-white mb-6">
              <div className="p-1.5 bg-purple-500/20 rounded-lg">
                <Terminal className="w-4 h-4 text-purple-400" />
              </div>
              {t.quickLinks}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.li
                  key={link.path}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                >
                  <Link
                    to={link.path}
                    className="group flex items-center text-sm text-gray-400 hover:text-white transition-all duration-300"
                  >
                    <span className="w-0 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 group-hover:w-4 transition-all duration-300 mr-0 group-hover:mr-2" />
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="flex items-center gap-2 text-lg font-semibold text-white mb-6">
              <div className="p-1.5 bg-blue-500/20 rounded-lg">
                <Zap className="w-4 h-4 text-blue-400" />
              </div>
              {t.services}
            </h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.05 }}
                  className="flex items-center gap-2 text-sm text-gray-400"
                >
                  <service.icon className="w-4 h-4 text-gray-500" />
                  {service.name}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="flex items-center gap-2 text-lg font-semibold text-white mb-6">
              <div className="p-1.5 bg-pink-500/20 rounded-lg">
                <Sparkles className="w-4 h-4 text-pink-400" />
              </div>
              {t.getInTouch}
            </h3>
            <div className="p-5 bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-sm rounded-2xl border border-white/10">
              <p className="text-sm text-gray-400 mb-4">
                {t.contactDesc}
              </p>
              <Link
                to="/visitor/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-sm font-medium rounded-xl shadow-lg shadow-purple-500/25 transition-all duration-300"
              >
                <Mail className="w-4 h-4" />
                {t.contact}
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="relative h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8">
          <div className="absolute left-1/2 -translate-x-1/2 -top-3 px-4 bg-slate-900">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="w-6 h-6 border-2 border-purple-500/50 border-t-purple-500 rounded-full"
            />
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row items-center justify-between gap-4"
        >
          {/* Copyright */}
          <p className="text-sm text-gray-500 font-mono">
            <span className="text-purple-400">const</span>{' '}
            <span className="text-blue-400">year</span>{' '}
            <span className="text-white">=</span>{' '}
            <span className="text-emerald-400">{new Date().getFullYear()}</span>;{' '}
            <span className="text-gray-600">// {t.copyright}</span>
          </p>

          {/* Made with */}
          <motion.p
            className="flex items-center gap-1 text-sm text-gray-500"
            whileHover={{ scale: 1.02 }}
          >
            {t.madeWith}
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <Heart className="w-4 h-4 text-red-500 fill-red-500 mx-1" />
            </motion.span>
            {t.and}
            <Coffee className="w-4 h-4 text-amber-500 mx-1" />
            {t.by}
            <span className="ml-1 font-semibold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Developer
            </span>
          </motion.p>
        </motion.div>
      </div>

      {/* Bottom Glow */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
    </footer>
  );
};

export default Footer;
